// Custom engraved tumblers. Targets: custom yeti 6,600, yeti with logo 4,400,
// personalized tumbler 3,600, custom stanley 2,900, yeti engraving 2,400,
// branded tumblers 1,300 ($8.59), engraved yeti 2,400 and laser engraved
// tumblers 2,400 (KD 0, added 2026-11-03). December peak. Quote-led pages rank here
// (Merchology #3 and #5 for "custom yeti with logo"). PrintAWorld is not
// affiliated with YETI or Stanley; the page says so.
const {
  quoted,
  setup,
  each,
  small,
  costAnswer,
  timingAnswer,
  minimumAnswer,
  ownItemsAnswer,
  shipAnswer,
  engravingItem,
} = require('./shared')

module.exports = engravingItem({
  key: 'tumblers',
  slug: 'tumblers',
  path: '/services/laser-engraving/tumblers/',
  noun: 'tumbler',
  crumb: 'Tumblers',
  title: 'Custom Engraved Tumblers, YETI & Stanley | PrintAWorld',
  description:
    'Laser engraved tumblers in NYC: custom YETI, Stanley and stainless tumblers with your logo or names, one or in bulk, engraved to bare steel.',
  eyebrow: 'Custom tumblers / laser engraved in New York',
  heading:
    'Custom engraved tumblers: your logo on YETI, Stanley and stainless drinkware.',
  answer: `PrintAWorld laser-engraves custom tumblers in New York: your logo or a name on YETI, Stanley and other stainless tumblers, mugs and water bottles. The laser removes the powder coat down to bright steel, so the logo won’t peel or fade like a print. Send tumblers you have, or we source them (${quoted.toLowerCase()}). Engraving is a ${setup} setup plus ${each(small, 'tumbler')}.`,
  cta: 'Get a tumbler quote',
  heroImage: 'logoTumblers',
  ideaImages: ['nameQuencher', 'wraparound', 'bulkCase'],
  images: {
    logoTumblers: {
      file: 'logo-tumblers',
      alt: 'Five powder-coated tumblers in different colors, each engraved with the same logo to bright steel',
      caption: 'One logo, any coating color',
    },
    nameQuencher: {
      file: 'name-quencher',
      alt: 'Cream 40 oz handled tumbler engraved with a first name and mountain line art',
      caption: 'A name on a 40 oz tumbler',
    },
    wraparound: {
      file: 'wraparound',
      alt: 'Black tumbler with a skyline line drawing engraved all the way around',
      caption: 'A design that wraps around',
    },
    bulkCase: {
      file: 'bulk-case',
      alt: 'Open carton of navy tumblers engraved with the same logo, packed in dividers',
      caption: 'A bulk run, packed to ship',
    },
  },
  intro: {
    eyebrow: 'Engraved, not printed',
    title: 'Engraved YETI and Stanley tumblers with your logo.',
    paragraphs: [
      'On a powder-coated tumbler, the laser burns away the color and leaves your logo in bare stainless steel. It doesn’t peel in the dishwasher the way a sticker or a cheap print does.',
      'Companies order them as swag and client gifts, teams and wedding parties order them with names. Send your own tumblers, or tell us the model and color and we source them.',
    ],
  },
  ideas: {
    eyebrow: 'Tumbler ideas',
    title: 'Logo tumblers, name tumblers and wraparound designs.',
  },
  uses: {
    eyebrow: 'Who orders them',
    title: 'Branded tumblers for companies, names for teams and events.',
    items: [
      {
        label: 'Company swag',
        title: 'Your logo on every tumbler',
        body: 'For employees, new hires and trade show tables. One design, engraved the same on each.',
      },
      {
        label: 'Client gifts',
        title: 'A tumbler they actually use',
        body: 'A YETI or Stanley with your logo, in a gift box, for the holidays or a closed deal.',
      },
      {
        label: 'Teams and clubs',
        title: 'A name on each',
        body: 'Your team logo plus each player’s name and number, from a list you send.',
      },
      {
        label: 'Weddings and parties',
        title: 'Gifts for the whole party',
        body: 'Names and the date for bridesmaids and groomsmen, or a gift for every guest.',
      },
      {
        label: 'Shops and brands',
        title: 'Tumblers to sell',
        body: 'Your artwork on blank tumblers for your store, priced per piece for each quantity.',
      },
    ],
  },
  materials: {
    link: 'Which tumblers',
    eyebrow: 'Which tumblers',
    title: 'Powder-coated, stainless and your own tumblers.',
    items: [
      {
        title: 'Powder-coated tumblers',
        body: 'YETI, Stanley and similar. The engraving cuts through the color to bright silver steel.',
      },
      {
        title: 'Bare stainless steel',
        body: 'Uncoated tumblers and flasks get a dark, permanent mark from our MOPA metal laser.',
      },
      {
        title: 'Color logos',
        body: 'Need your logo in color? We UV print it on the same tumbler, or pair print and engraving.',
      },
      {
        title: 'Your own tumblers',
        body: 'Already bought them, or your supplier ships them? We engrave them after testing one.',
      },
    ],
  },
  process: {
    title: 'From your logo to a case of tumblers.',
    note: 'Standard lead time is 2–3 weeks after the tumblers reach us. For holiday gifts, order in November.',
    steps: [
      {
        title: 'Send the logo and tumblers',
        body: 'Your logo, the tumbler model and color, the count, and where the logo goes.',
      },
      {
        title: 'Approve a test',
        body: 'We send a proof and engrave a spare, so you see the real mark first.',
      },
      {
        title: 'We engrave the run',
        body: 'Each tumbler is engraved in the same spot and checked, with names matched to your list.',
      },
      {
        title: 'Pack and ship',
        body: 'Back in the cartons they came in, shipped anywhere in the U.S. or picked up in NYC.',
      },
    ],
  },
  pricing: {
    title: 'Custom tumbler pricing, per piece.',
    cta: 'Price my tumblers',
    rows: [
      { label: 'Engraving, per tumbler', value: small },
      { label: 'The tumblers, if we source them', value: quoted },
      { label: 'Second side or wraparound', value: quoted },
      { label: 'Color by UV printing', value: quoted },
      { label: 'Rush', value: 'Case by case' },
    ],
  },
  bulk: {
    eyebrow: 'Branded tumblers in bulk',
    title: 'Hundreds of logo tumblers, every one in the same spot.',
    paragraphs: [
      'For bulk orders we engrave and approve one tumbler, then run the rest on the same setup, so the logo sits in the same place on every piece.',
      'Ordering through a promo supplier? Have them ship the blank tumblers to us and we engrave them.',
    ],
    cta: 'Plan a bulk order',
  },
  faqTitle: 'Custom tumbler questions, answered.',
  faqs: [
    {
      question: 'How much does it cost to engrave a tumbler?',
      answer: costAnswer(
        small,
        `A second side, a wraparound design, color printing and the tumblers themselves are ${quoted.toLowerCase()}.`
      ),
    },
    {
      question: 'Can you engrave a YETI or Stanley I already have?',
      answer: `Yes. ${ownItemsAnswer('tumblers')} PrintAWorld is an independent engraver, not affiliated with YETI or Stanley.`,
    },
    {
      question: 'Will the engraving come off in the dishwasher?',
      answer:
        'No. The logo is the bare steel under the coating, not ink on top, so there is nothing to peel or fade. Check the tumbler maker’s own care advice for the coating.',
    },
    {
      question: 'Can the engraving be in color?',
      answer:
        'Engraving shows the steel, so it is silver on a coated tumbler and dark on bare steel. For a color logo we UV print it on the tumbler instead.',
    },
    {
      question: 'Can you engrave all the way around?',
      answer:
        'Often, depending on the tumbler shape and handle. Wraparound and two-sided designs are quoted per project after a test.',
    },
    {
      question: 'Can each tumbler have a different name?',
      answer:
        'Yes. Send the names in a spreadsheet; we keep the logo the same and engrave a name on each.',
    },
    {
      question: 'Is there a minimum order?',
      answer: minimumAnswer('tumbler'),
    },
    {
      question: 'How long does it take?',
      answer: timingAnswer('For holiday gifts, send the request in November.'),
    },
    {
      question: 'Do you ship?',
      answer: shipAnswer,
    },
  ],
  quote: {
    title: 'Tell us about your tumblers.',
    body: 'Send the logo or names, the tumbler model and color, how many and when you need them. We’ll reply with a written quote.',
    checklist: [
      'Your logo (vector if you have it) or the names',
      'The tumbler model and color, or a link',
      'How many, or several quantities to price',
      'Your in-hand date and delivery ZIP',
    ],
  },
  moreCards: ['corporateGifts', 'bottleEngraving', 'leatherPatchHats'],
  schema: {
    name: 'Custom Engraved Tumblers',
    alternateName: [
      'Custom YETI tumblers with logo',
      'Custom Stanley tumblers',
      'Branded tumblers',
    ],
    serviceType: 'Laser-engraved custom tumblers',
  },
})
